import { Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TestIds } from '../../../assets/testIds';
import { useOptimizelyFlag } from '../../../common/hooks/useOptimizelyFlag';
import { AiCoachBetaAccessFlag, AiCoachFlag } from '../../../common/OptimizelyFlagsConstants';
import { HomeCoachBar } from './HomeCoachBar';
import { HomeHighlights } from './HomeHighlights';
import { HomeUpcoming } from './HomeUpcoming';

/**
 * Redesigned home page (SN-1046), shown behind the edgeHomeNewDesign flag. The
 * previous home feed now lives on the Resources page.
 */
export function HomePage() {
  const theme = useTheme();
  const { t } = useTranslation();
  // The coach bar is only for members who can reach the AI Coach chat: the Coaching
  // page (aiCoach) and the chat itself (aiCoachBetaAccess).
  const { decision: aiCoachDecision, isDecisionReady: isAiCoachDecisionReady } =
    useOptimizelyFlag({ flag: AiCoachFlag });
  const { decision: betaAccessDecision, isDecisionReady: isBetaAccessDecisionReady } =
    useOptimizelyFlag({ flag: AiCoachBetaAccessFlag });
  const hasCoachAccess = isAiCoachDecisionReady && !!aiCoachDecision?.enabled &&
    isBetaAccessDecisionReady && !!betaAccessDecision?.enabled;

  return (
    <Box sx={{ width: '100%' }}>
      {hasCoachAccess && <HomeCoachBar />}
      <Box sx={{
        padding: `${theme.spacing(3.5)} ${theme.spacing(4)}`,
        // On mobile the top-nav layout's container already pads the page.
        [theme.breakpoints.down('sm')]: { padding: `${theme.spacing(2)} 0` },
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
          <Typography
            component="h1"
            data-testid={TestIds.homePage.heading}
            sx={{ fontSize: 24, fontWeight: 500, color: theme.palette.text.primary }}
          >
            {t('homePage.heading')}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.grey[600] }}>
            {t('homePage.subtitle')}
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          gap: theme.spacing(2.5),
          alignItems: 'flex-start',
          [theme.breakpoints.down('lg')]: { flexDirection: 'column', alignItems: 'stretch' },
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <HomeHighlights />
          </Box>
          <Box sx={{
            width: 427,
            flexShrink: 0,
            // Lines the panel up with the first highlight card, below the Highlights heading
            marginTop: theme.spacing(8.5),
            [theme.breakpoints.down('lg')]: { width: 'auto', marginTop: theme.spacing(2) },
          }}>
            <HomeUpcoming />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
